import type React from 'react';

interface CardProps {
  suit: string;
  rank: number;
  faceDown?: boolean;
  size?: 'sm' | 'md' | 'lg';
  playable?: boolean;
  disabled?: boolean;
  highlighted?: boolean;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const RANK_CODE: Record<number, string> = {
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: 'T',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

const SUIT_CODE: Record<string, string> = {
  spades: 'S',
  hearts: 'H',
  diamonds: 'D',
  clubs: 'C',
};

const SUIT_NAMES: Record<string, string> = {
  spades: 'Spades',
  hearts: 'Hearts',
  diamonds: 'Diamonds',
  clubs: 'Clubs',
};

const RANK_LABEL: Record<number, string> = {
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

const SUIT_SYMBOL: Record<string, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

const CARD_BASE = `${import.meta.env.BASE_URL}/cards/`.replace('//', '/');

function cardSrc(suit: string, rank: number, faceDown: boolean): string {
  if (faceDown) return `${CARD_BASE}1B.svg`;
  const r = RANK_CODE[rank] ?? '2';
  const s = SUIT_CODE[suit] ?? 'S';
  return `${CARD_BASE}${r}${s}.svg`;
}

export function Card({
  suit,
  rank,
  faceDown = false,
  size = 'md',
  playable = false,
  disabled = false,
  highlighted = false,
  selected = false,
  onClick,
  className = '',
  style,
}: CardProps) {
  const label = RANK_LABEL[rank] ?? String(rank);
  const suitName = SUIT_NAMES[suit] ?? suit;
  const ariaLabel = faceDown
    ? 'Face-down card'
    : `${label} of ${suitName}${playable ? ' — playable' : ''}`;

  const classes = [
    'card-visual',
    `card-${size}`,
    playable ? 'card-playable' : '',
    disabled ? 'card-disabled' : '',
    highlighted ? 'card-highlighted' : '',
    selected ? 'card-selected' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      className={classes}
      style={style}
      onClick={onClick}
      disabled={disabled || !playable}
      type="button"
      aria-label={ariaLabel}
    >
      <img src={cardSrc(suit, rank, faceDown)} alt="" aria-hidden="true" draggable="false" />
    </button>
  );
}

export function CardBack({
  size = 'sm',
  className = '',
  style,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
}) {
  return <Card suit="spades" rank={2} faceDown size={size} className={className} style={style} />;
}

export function formatCardText(card: { suit: string; rank: number }): string {
  const suit = SUIT_SYMBOL[card.suit] ?? card.suit;
  const rank = RANK_LABEL[card.rank] ?? String(card.rank);
  return `${rank}${suit}`;
}
